import React, { useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { portfolioManagement, valueCreationGroup, seniorAdvisory } from '../data/teamData';
import { Linkedin, X } from 'lucide-react';

export const Team = () => {
  const { language } = useLanguage();
  const [selectedMember, setSelectedMember] = useState(null);
  const [filters, setFilters] = useState({
    teams: [],
    positions: []
  });


  // Combine all team members - Remove duplicates by name
  const allMembers = useMemo(() => {
    const combined = [
      ...portfolioManagement,
      ...valueCreationGroup,
      ...seniorAdvisory
    ];

    const seen = new Set();
    return combined.filter(member => {
      if (seen.has(member.name)) {
        return false;
      }
      seen.add(member.name);
      return true;
    });
  }, []);

  // Filter options
  const teamOptions = [
    { value: 'portfolio-management', label: 'Portfolio Management Group' },
    { value: 'value-creation', label: 'Value Creation Group' },
    { value: 'senior-advisory', label: language === 'fr' ? 'Groupe de conseillers seniors' : 'Senior Advisory Group' }
  ];

  const positionOptions = [
    { value: 'partner', label: 'Partner' },
    { value: 'principal', label: 'Principal' },
    { value: 'officer', label: 'Officer' },
    { value: 'associate', label: 'Associate' },
    { value: 'senior-adviser', label: 'Senior Advisor' }
  ];

  const handleCheckboxChange = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  };

  const clearAllFilters = () => {
    setFilters({ teams: [], positions: [] });
  };

  // Filtered and sorted members
  const displayedMembers = useMemo(() => {
    let filtered = allMembers;

    if (filters.teams.length > 0) {
      filtered = filtered.filter(member => filters.teams.includes(member.team));
    }

    if (filters.positions.length > 0) {
      filtered = filtered.filter(member => filters.positions.includes(member.position));
    }

    filtered = [...filtered].sort((a, b) => {
      const surnameA = a.name.split(' ').slice(-1)[0];
      const surnameB = b.name.split(' ').slice(-1)[0];
      return surnameA.localeCompare(surnameB);
    });

    return filtered;
  }, [allMembers, filters]);

  const TeamMemberCard = ({ member }) => (
    <div
      onClick={() => setSelectedMember(member)}
      className="group cursor-pointer bg-white border border-gray-300 transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
      style={{
        fontFamily: "'Eurostile', 'Arial', sans-serif",
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderColor: '#e5e5e5'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(91, 103, 112, 0.2)';
        e.currentTarget.style.borderColor = '#5b6770';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        e.currentTarget.style.borderColor = '#e5e5e5';
      }}
    >
      <div className="aspect-square overflow-hidden bg-gray-100">
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
          style={{
            transform: `scale(${member.imageScale || 1})`,
            objectPosition: member.imagePosition || 'center top'
          }}
        />
      </div>
      <div className="p-0 flex-1 flex flex-col">
        {/* Name */}
        <div className="px-4 pt-5 pb-1">
          <h3 className="text-lg font-bold text-[#0a1f35] mb-0" style={{ fontWeight: 700 }}>
            {member.name}
          </h3>
        </div>
        {/* Titles */}
        <div className="px-4 pb-2">
          <p className="text-sm text-[#5b677c]" style={{ fontWeight: 500, fontSize: '12px', lineHeight: 1.4 }}>
            {member.title}
          </p>
          {member.title2 && (
            <p className="text-sm text-[#5b677c]" style={{ fontWeight: 500, fontSize: '12px', lineHeight: 1.4 }}>
              {member.title2}
            </p>
          )}
        </div>
        {/* LinkedIn */}
        <div className="px-4 pb-4 flex items-center justify-end mt-auto">
          {member.linkedin && (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[#5b677c] hover:text-[#2a6cae] transition-colors duration-200"
              aria-label={`View ${member.name}'s LinkedIn profile`}
            >
              <Linkedin size={18} />
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f4f5f7', fontFamily: "'Eurostile', 'Arial', sans-serif" }}>
      {/* Hero Banner with Image */}
      <section 
        className="relative h-[500px] flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#0a1f35' }}
      >
        <div 
          className="absolute inset-0 bg-cover grayscale"
          style={{
            backgroundImage: 'url(/hero-team.jpg)',
            backgroundPosition: 'center 45%',
            filter: 'grayscale(100%)'
          }}
        />
        <div 
          className="absolute inset-0" 
          style={{ backgroundColor: 'rgba(10, 31, 53, 0.3)' }}
        />
        
        <div className="relative z-10 text-center px-4">
          <h1
            className="text-6xl text-white mb-4"
            style={{
              fontFamily: "'Bank Gothic', 'Impact', sans-serif",
              fontWeight: 900,
              letterSpacing: '0.02em',
              textTransform: 'uppercase'
            }}
          >
            {language === 'fr' ? 'ÉQUIPE' : 'TEAM'}
          </h1>
          <p
            className="text-xl lg:text-2xl text-white text-center max-w-4xl mx-auto font-bold"
            style={{
              fontFamily: "'Eurostile', 'Arial', sans-serif",
              lineHeight: 1.6
            }}
          >
            {language === 'fr'
              ? "Une équipe à la diversité unique : expertise industrielle de haut niveau, expérience d'investissement prouvée et un track record au cœur de l'action."
              : "A uniquely diverse team combining deep industrial expertise, proven investment experience, and a track record at the heart of the action."}
          </p>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Left Sidebar - Filters */}
            <div className="lg:w-72 flex-shrink-0">
              <div className="bg-white shadow-xl lg:sticky lg:top-32">
                {/* Filter Header */}
                <div className="p-6 border-b border-gray-100">
                  <h2
                    className="text-lg text-[#0a1f35]"
                    style={{ fontFamily: "'Novecento Sans Wide', sans-serif", fontWeight: 600, letterSpacing: '0.1em' }}
                  >
                    {language === 'fr' ? 'FILTRES' : 'FILTERS'}
                  </h2>
                </div>

                <div className="p-6 space-y-8">
                  {/* TEAM GROUP */}
                  <div>
                    <h3
                      className="text-xs text-[#5b6770] uppercase tracking-widest mb-4"
                      style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                    >
                      {language === 'fr' ? 'Groupe' : 'Group'}
                    </h3>
                    <div className="space-y-2">
                      {teamOptions.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center gap-3 cursor-pointer group py-1"
                          onClick={() => handleCheckboxChange('teams', option.value)}
                        >
                          <div className={`w-4 h-4 border-2 flex items-center justify-center transition-all ${
                            filters.teams.includes(option.value)
                              ? 'border-[#2a6cae] bg-[#2a6cae]'
                              : 'border-[#5b6770]/40 group-hover:border-[#2a6cae]'
                          }`}>
                            {filters.teams.includes(option.value) && (
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-[#5b6770] group-hover:text-[#0a1f35] transition-colors">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-100"></div>

                  {/* POSITION */}
                  <div>
                    <h3
                      className="text-xs text-[#5b6770] uppercase tracking-widest mb-4"
                      style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                    >
                      {language === 'fr' ? 'Poste' : 'Position'}
                    </h3>
                    <div className="space-y-2">
                      {positionOptions.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center gap-3 cursor-pointer group py-1"
                          onClick={() => handleCheckboxChange('positions', option.value)}
                        >
                          <div className={`w-4 h-4 border-2 flex items-center justify-center transition-all ${
                            filters.positions.includes(option.value)
                              ? 'border-[#2a6cae] bg-[#2a6cae]'
                              : 'border-[#5b6770]/40 group-hover:border-[#2a6cae]'
                          }`}>
                            {filters.positions.includes(option.value) && (
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-[#5b6770] group-hover:text-[#0a1f35] transition-colors">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* Divider */}
                </div>

                {/* Clear Button */}
                <div className="p-6 border-t border-gray-100">
                  <button
                    onClick={clearAllFilters}
                    className="w-full py-3 text-xs uppercase tracking-widest text-[#5b6770] hover:text-white hover:bg-[#0a1f35] border border-[#5b6770]/30 hover:border-[#0a1f35] transition-all duration-300"
                    style={{ fontFamily: "'Novecento Sans Wide', sans-serif" }}
                  >
                    {language === 'fr' ? 'Effacer les filtres' : 'Clear Filters'}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Content - Team Grid */}
            <div className="flex-1">
              {/* Team Grid */}
              <div
                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[30px] transition-all duration-400"
                style={{ transition: 'all 0.4s ease' }}
              >
                {displayedMembers.map((member, idx) => (
                  <div
                    key={member.name + idx}
                    className="animate-fade-in"
                    style={{
                      animation: 'fadeIn 0.4s ease-out',
                      animationDelay: `${idx * 0.05}s`,
                      animationFillMode: 'both'
                    }}
                  >
                    <TeamMemberCard member={member} />
                  </div>
                ))}
              </div>
              {displayedMembers.length === 0 && (
                <div className="text-center py-20 bg-white shadow-lg">
                  <p className="text-[#5b677c] text-lg">
                    {language === 'fr' ? 'Aucun membre trouvé' : 'No members found'}
                  </p>
                  <p className="text-sm text-[#5b6770]/60 mt-2">
                    {language === 'fr' ? 'Essayez d\'ajuster vos filtres' : 'Try adjusting your filters'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedMember && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
          onClick={() => setSelectedMember(null)}
        >
          <div
            className="bg-white max-w-[900px] w-full max-h-[90vh] overflow-y-auto relative animate-modal-in"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: "'Eurostile', 'Arial', sans-serif" }}
          >
            <button
              onClick={() => setSelectedMember(null)}
              className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-[#5b677c] hover:text-[#0a1f35] transition-colors duration-200"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
            <div className="flex flex-col md:flex-row p-12">
              <div className="md:w-[310px] flex-shrink-0 mb-6 md:mb-0 md:mr-10">
                <img
                  src={selectedMember.image}
                  alt={selectedMember.name}
                  className="w-full aspect-square object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-3xl font-bold text-[#0a1f35] mb-4" style={{ fontWeight: 700 }}>
                      {selectedMember.name}
                    </h2>
                    {/* Titles - one per line with bullet points */}
                    {(() => {
                      const titles = selectedMember.titles;
                      return titles && titles.length > 0 ? (
                        <div className="mb-4">
                          {titles.map((t, idx) => (
                            <p key={idx} className="text-base text-[#5b677c] mb-1">
                              • {t}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-base text-[#5b677c] mb-4">
                          {selectedMember.title}
                        </p>
                      );
                    })()}
                  </div>
                  {selectedMember.linkedin && (
                    <a
                      href={selectedMember.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#5b677c] hover:text-[#5b6770] transition-colors duration-200 ml-4"
                      aria-label={`View ${selectedMember.name}'s LinkedIn profile`}
                    >
                      <Linkedin size={24} />
                    </a>
                  )}
                </div>
                {/* Bio */}
                {selectedMember.bio && (
                  <p className="text-[15px] text-[#0a1f35] leading-relaxed mb-6">
                    {language === 'fr' ? (selectedMember.bioFr || selectedMember.bio) : selectedMember.bio}
                  </p>
                )}
                {/* Education */}
                {selectedMember.education && (
                  <p className="text-[15px] text-[#0a1f35] leading-relaxed mb-6">
                    {language === 'fr' ? (selectedMember.educationFr || selectedMember.education) : selectedMember.education}
                  </p>
                )}
                {/* Tag Categories */}
                <div className="mt-4 space-y-3">
                  {selectedMember.language && selectedMember.language.length > 0 && (
                    <div>
                      <p className="text-xs text-[#5b677c] mb-1 uppercase tracking-wide" style={{ fontWeight: 600 }}>{language === 'fr' ? 'Langues' : 'Languages'}</p>
                      <div className="flex flex-wrap gap-2">
                        {(language === 'fr' ? (selectedMember.languageFr || selectedMember.language) : selectedMember.language).map((item, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 text-sm"
                            style={{ backgroundColor: 'rgba(91, 103, 112, 0.08)', color: '#5b6770' }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMember.sectorFocus && selectedMember.sectorFocus.length > 0 && (
                    <div>
                      <p className="text-xs text-[#5b677c] mb-1 uppercase tracking-wide" style={{ fontWeight: 600 }}>{language === 'fr' ? 'Secteurs d\'expertise' : 'Sector Focus'}</p>
                      <div className="flex flex-wrap gap-2">
                        {(language === 'fr' ? (selectedMember.sectorFocusFr || selectedMember.sectorFocus) : selectedMember.sectorFocus).map((item, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 text-sm"
                            style={{ backgroundColor: 'rgba(10, 31, 53, 0.1)', color: '#0a1f35' }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMember.countryFocus && selectedMember.countryFocus.length > 0 && (
                    <div>
                      <p className="text-xs text-[#5b677c] mb-1 uppercase tracking-wide" style={{ fontWeight: 600 }}>{language === 'fr' ? 'Pays d\'expertise' : 'Country Focus'}</p>
                      <div className="flex flex-wrap gap-2">
                        {(language === 'fr' ? (selectedMember.countryFocusFr || selectedMember.countryFocus) : selectedMember.countryFocus).map((item, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 text-sm"
                            style={{ backgroundColor: 'rgba(91, 103, 112, 0.1)', color: '#5b6770' }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMember.functionalExpertise && selectedMember.functionalExpertise.length > 0 && (
                    <div>
                      <p className="text-xs text-[#5b677c] mb-1 uppercase tracking-wide" style={{ fontWeight: 600 }}>{language === 'fr' ? 'Expertise fonctionnelle' : 'Functional Expertise'}</p>
                      <div className="flex flex-wrap gap-2">
                        {(language === 'fr' ? (selectedMember.functionalExpertiseFr || selectedMember.functionalExpertise) : selectedMember.functionalExpertise).map((item, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 text-sm"
                            style={{ backgroundColor: 'rgba(91, 103, 112, 0.1)', color: '#5b6770' }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modal-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-modal-in {
          animation: modal-in 0.3s ease;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          opacity: 0;
        }
      `}</style>
    </div>
  );
};
